import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Check, Copy, X } from "lucide-react";
import { useOutletContext } from "react-router-dom";
import type { AuthUser } from "@match/shared";

/*
 * Primitive condivise dalle pagine.
 *
 * I pulsanti primari sono neutri, non indaco: indaco e ambra significano prop e
 * broker, e usarli come colore d'azione diluirebbe l'informazione.
 */

// ---------------------------------------------------------------------------
// Utente e permessi
// ---------------------------------------------------------------------------

export function useCurrentUser(): AuthUser {
  return useOutletContext<AuthUser>();
}

/**
 * Sta guardando un CLIENTE la sua area, e non un operatore il gestionale.
 *
 * Serve alle pagine condivise — i cicli — per non mettergli in vista maniglie
 * che non girano: «Scheda cliente», «Apri la fase», «Nuova fase» portano a
 * rotte che per lui sono 403. **Non è una barriera di sicurezza**: quella sta
 * nel backend (`lib/client-scope.ts`), allowlist e filtro dentro la query.
 */
export function useIsClient(): boolean {
  return useCurrentUser().role === "CLIENT";
}

/** Solo ADMIN: le credenziali MT5, come `requireRole(["ADMIN"])` lato backend. */
export function useIsAdmin(): boolean {
  return useCurrentUser().role === "ADMIN";
}

/** Scrittura: OPERATORE e ADMIN, come requireRole lato backend. */
export function useCanWrite(): boolean {
  const user = useCurrentUser();
  return user.role === "ADMIN" || user.role === "OPERATORE";
}

// ---------------------------------------------------------------------------
// Pulsanti e campi
// ---------------------------------------------------------------------------

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";

const BUTTON: Record<ButtonVariant, string> = {
  primary: "bg-ink text-page hover:bg-ink/90",
  secondary: "border border-line bg-raised text-ink hover:border-muted/60",
  ghost: "text-muted hover:bg-raised hover:text-ink",
  danger: "border border-loss/40 text-loss hover:bg-loss/10",
};

export function Button({
  variant = "secondary",
  size = "md",
  className = "",
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: "sm" | "md" }) {
  return (
    <button
      type="button"
      {...props}
      className={[
        "inline-flex items-center justify-center gap-1.5 rounded font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50",
        size === "sm" ? "px-2 py-1 text-xs" : "px-3 py-1.5 text-sm",
        BUTTON[variant],
        className,
      ].join(" ")}
    />
  );
}

export const inputClass =
  "w-full rounded border border-line bg-raised px-3 py-2 text-sm text-ink outline-none placeholder:text-faint focus:border-muted disabled:opacity-60";

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: ReactNode;
  children: (id: string) => ReactNode;
}) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm text-muted">
        {label}
      </label>
      {children(id)}
      {hint && <p className="mt-1 text-xs text-faint">{hint}</p>}
    </div>
  );
}

export function ErrorNote({ children }: { children: ReactNode }) {
  if (!children) return null;
  return (
    <p role="alert" className="rounded border border-loss/40 bg-loss/10 px-3 py-2 text-sm text-loss">
      {children}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Pannello laterale
// ---------------------------------------------------------------------------

export function SidePanel({
  open,
  title,
  subtitle,
  onClose,
  children,
  footer,
}: {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-page/70" onClick={onClose} aria-hidden />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative flex h-full w-full max-w-md flex-col border-l border-line bg-surface shadow-2xl"
      >
        <header className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
          <div>
            <h2 className="text-base font-semibold tracking-tight">{title}</h2>
            {subtitle && <p className="mt-0.5 text-sm text-muted">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="rounded p-1 text-muted hover:bg-raised hover:text-ink" aria-label="Chiudi">
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">{children}</div>
        {footer && <footer className="flex justify-end gap-2 border-t border-line px-5 py-3">{footer}</footer>}
      </aside>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Valori
// ---------------------------------------------------------------------------

/** Tratto per "non applicabile". */
export function Dash({ title }: { title?: string }) {
  return (
    <span className="text-faint" title={title}>
      —
    </span>
  );
}

/** Valore rilevato dal gateway: corsivo e grigio. Quelli impostati a mano restano in tondo. */
export function Auto({ children, title = "Rilevato dal gateway" }: { children: ReactNode; title?: string }) {
  return (
    <span className="italic text-muted" title={title}>
      {children}
    </span>
  );
}

export function ConnectionState({
  connected,
  error,
  compact = false,
}: {
  connected: boolean;
  error?: string | null;
  compact?: boolean;
}) {
  const tone = connected ? "bg-gain" : error ? "bg-loss" : "bg-faint";
  const label = connected ? "Connesso" : error ? "Errore di connessione" : "Disconnesso";
  return (
    <span className="inline-flex items-center gap-1.5 text-sm" title={error ?? label}>
      <span className={`h-2 w-2 shrink-0 rounded-full ${tone}`} aria-hidden />
      {compact ? <span className="sr-only">{label}</span> : <span className={connected ? "text-ink" : "text-muted"}>{label}</span>}
    </span>
  );
}

/** CID casuale: 32 caratteri esadecimali, come li vuole Mirror. */
export function randomComputerId(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("").toUpperCase();
}

export const COMPUTER_ID_RE = /^[0-9a-fA-F]{32}$/;

// ---------------------------------------------------------------------------
// Modifica in linea
// ---------------------------------------------------------------------------

/**
 * Cella modificabile: si clicca, si scrive, Invio o uscita salvano, Esc annulla.
 * `display` è ciò che si vede a riposo; `initial` il testo da cui parte l'editor.
 */
export function InlineEdit({
  display,
  initial,
  onSave,
  disabled,
  placeholder,
  inputMode,
  ariaLabel,
}: {
  display: ReactNode;
  initial: string;
  onSave: (value: string) => Promise<unknown>;
  disabled?: boolean;
  placeholder?: string;
  inputMode?: "text" | "numeric";
  ariaLabel: string;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) ref.current?.select();
  }, [editing]);

  if (disabled) return <>{display}</>;

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => {
          setValue(initial);
          setError(null);
          setEditing(true);
        }}
        className="-mx-1 max-w-full rounded px-1 text-left hover:bg-raised"
        aria-label={ariaLabel}
        title={error ?? "Clicca per modificare"}
      >
        {display}
      </button>
    );
  }

  const commit = async () => {
    if (value === initial) return setEditing(false);
    setBusy(true);
    try {
      await onSave(value);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Salvataggio non riuscito");
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="block">
      <input
        ref={ref}
        value={value}
        disabled={busy}
        inputMode={inputMode}
        placeholder={placeholder}
        aria-label={ariaLabel}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if (e.key === "Enter") void commit();
          if (e.key === "Escape") setEditing(false);
        }}
        className="w-full min-w-24 rounded border border-muted bg-raised px-1.5 py-0.5 text-sm outline-none"
      />
      {error && <span className="mt-0.5 block text-xs text-loss">{error}</span>}
    </span>
  );
}

/** Copia un valore negli appunti. Per gli identificatori macchina: login, ticket, CID. */
export function CopyButton({ value, label }: { value: string; label: string }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch {
      /* permesso negato: il valore resta selezionabile a mano */
    }
  };
  return (
    <button
      type="button"
      onClick={() => void copy()}
      className="rounded p-1 text-muted hover:bg-raised hover:text-ink"
      aria-label={`Copia ${label}`}
      title={done ? "Copiato" : `Copia ${label}`}
    >
      {done ? <Check className="h-3.5 w-3.5 text-gain" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}
